<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useDeviceStore } from './store'
import { Device } from './models/device'
import { useDeviceLoader } from '@/composables/useDeviceLoader'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import type { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'

// Store
const deviceStore = useDeviceStore()
const deviceLoader = useDeviceLoader()
const measurementsStore = useMeasurementsStore()

// Loading state
const loading = ref(false)
const loadingMeasurements = ref<Record<string, boolean>>({})

// Device samples por deviceId (para imagen y datos reales)
const deviceSamplesMap = ref<Record<string, DeviceSample[]>>({})

// Computed para obtener los dispositivos del store
const devices = computed(() => deviceStore.devices)

// Helper functions
const parseMetadata = (metadataStr: string): Record<string, unknown> => {
    try {
        return typeof metadataStr === 'string' ? JSON.parse(metadataStr) : {}
    } catch (e) {
        return {}
    }
}

const getDeviceImage = (device: Device): string => {
    // Buscar la última device sample con imageUrl
    const samples = deviceSamplesMap.value[device.id] || []
    const sampleWithImage = samples.find(s => s.imageUrl && s.imageUrl.trim() !== '')
    if (sampleWithImage) return sampleWithImage.imageUrl

    // Fallback a imágenes del device
    if (device.images && device.images.length > 0) {
        const sortedImages = device.images.sort((a, b) => a.position - b.position)
        return sortedImages[0].src
    }
    return '/images/2.png'
}

const getDeviceStatus = (device: Device) => {
    const metadata = parseMetadata(device.metadata)
    const status = (metadata.status as string | undefined) || (device.active ? 'connected' : 'disconnected')
    const statusMap: Record<string, { text: string; color: string }> = {
        'connected': { text: 'Conectado', color: '#689F38' },
        'disconnected': { text: 'Desconectado', color: '#C62828' },
        'sync_delay': { text: 'Retraso sincronización', color: '#FFC107' },
        'maintenance': { text: 'Mantenimiento', color: '#FF5722' }
    }
    return statusMap[status] || statusMap.disconnected
}

const getGrowthPercentage = (device: Device): number => {
    const samples = deviceSamplesMap.value[device.id] || []
    // Última sample con growthPercent válido
    const lastWithGrowth = samples.find(s => s.managed && s.growthPercent != null)
    return lastWithGrowth ? lastWithGrowth.growthPercent : 0
}

const getExpectedMeasurements = (device: Device) => {
    const samples = deviceSamplesMap.value[device.id] || []
    const actual = samples.length
    const metadata = parseMetadata(device.metadata)
    const expected = (metadata.expectedMeasurements as number | undefined) || 0
    return { actual, expected }
}

const getCompletionPercentage = (device: Device): number => {
    const samples = deviceSamplesMap.value[device.id] || []
    const managed = samples.filter(s => s.managed).length
    const total = samples.length
    if (total === 0) return 0
    return Math.round((managed / total) * 100)
}

const getLastUpdate = (device: Device): string => {
    const samples = deviceSamplesMap.value[device.id] || []
    if (samples.length === 0) return 'No disponible'
    // Ordenar por time desc y tomar la más reciente
    const sorted = [...samples].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit'
    }).format(new Date(sorted[0].time))
}

const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
    }).format(new Date(date))
}

const getTotalUpdates = (device: Device): number => {
    const samples = deviceSamplesMap.value[device.id] || []
    return samples.length
}

const getObservations = (device: Device): string => {
    const samples = deviceSamplesMap.value[device.id] || []
    const lastWithObs = samples.find(s => s.observation && s.observation.trim() !== '')
    return lastWithObs ? lastWithObs.observation : 'Sin observaciones'
}

// Función para cargar device samples de un dispositivo específico
const loadDeviceSamples = async (deviceId: string) => {
    if (loadingMeasurements.value[deviceId]) return

    loadingMeasurements.value[deviceId] = true
    try {
        const samples = await measurementsStore.getMeasurementsByDeviceId(deviceId)
        deviceSamplesMap.value[deviceId] = samples
    } finally {
        loadingMeasurements.value[deviceId] = false
    }
}

// Lifecycle
onMounted(async () => {
    loading.value = true
    try {
        // Usar carga condicional por rol
        await deviceLoader.loadDevicesByRole()

        // Cargar device samples para todos los dispositivos
        const samplePromises = devices.value.map(device =>
            loadDeviceSamples(device.id)
        )
        await Promise.all(samplePromises)

    } catch (err) {
        console.error('Error loading devices view:', err)
    } finally {
        loading.value = false
    }
})
</script>

<template>
    <v-container fluid>
        <div class="mb-8 text-center">
            <h1 class="text-h2 font-weight-bold primary--text mb-2">Dispositivos</h1>
            <div class="d-flex justify-center align-center">
                <v-divider class="mx-4" width="50"></v-divider>
                <span class="text-subtitle-1 text-grey">Monitoreo y control</span>
                <v-divider class="mx-4" width="50"></v-divider>
            </div>
            <p class="text-body-1 mt-4 text-grey-darken-1 max-width-800 mx-auto">
                Gestione sus dispositivos de medición conectados. Revise el estado, visualice datos
                en tiempo real.
            </p>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="mt-4 text-body-1">Cargando dispositivos...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="devices.length === 0" class="text-center">
            <v-icon size="64" color="grey-lighten-1">mdi-devices</v-icon>
            <p class="mt-4 text-h6 text-grey">No hay dispositivos registrados</p>
            <p class="text-body-2 text-grey">Los dispositivos aparecerán aquí una vez que se registren en el sistema.</p>
        </div>

        <!-- Devices Grid -->
        <v-row v-else>
            <v-col cols="12" md="4" v-for="device in devices" :key="device.id">
                <v-card class="rounded-lg overflow-hidden" elevation="3">
                    <!-- Header Image -->
                    <div class="position-relative">
                        <v-img :src="getDeviceImage(device)" height="250" cover class="gradient-overlay">
                            <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
                                </v-row>
                            </template>
                        </v-img>

                        <!-- Avatar that overlaps image and card content -->
                        <v-avatar size="80" class="avatar-overlap">
                            <v-avatar color="#E8F5E9" size="72">
                                <v-icon color="#4CAF50" size="36">mdi-sprout</v-icon>
                            </v-avatar>
                        </v-avatar>
                    </div>

                    <!-- Card content with spacing for avatar -->
                    <v-card-item class="pt-8 mt-6">
                        <v-card-title class="text-h6 font-weight-bold px-0">{{ device.name }}</v-card-title>
                        <v-card-subtitle class="px-0">{{ device.extendedName }}</v-card-subtitle>

                        <template v-slot:append>
                            <v-btn icon :to="`/devices/${device.id}`">
                                <v-icon>mdi-arrow-right</v-icon>
                            </v-btn>
                        </template>
                    </v-card-item>

                    <!-- Growth and Dates info -->
                    <v-card-text class="px-4 pb-0">
                        <div class="d-flex">
                            <v-sheet class="pa-3 rounded bg-grey-lighten-4 flex-grow-1 mr-2">
                                <div class="text-body-1 font-weight-medium">{{ getGrowthPercentage(device) }}%</div>
                                <div class="text-caption text-grey">Crecimiento</div>
                            </v-sheet>

                            <v-sheet class="pa-3 rounded bg-grey-lighten-4 flex-grow-1">
                                <div class="d-flex flex-column">
                                    <div class="text-body-1 font-weight-medium">Fecha inicio: {{ formatDate(device.initDate) }}</div>
                                    <div class="text-body-2 font-weight-medium">Última actualización: {{ getLastUpdate(device) }}</div>
                                </div>
                            </v-sheet>
                        </div>
                    </v-card-text>

                    <!-- Description -->
                    <v-card-text class="pt-3">
                        <p class="text-body-2">{{ getObservations(device) }}</p>
                    </v-card-text>

                    <!-- Divider -->
                    <v-divider></v-divider>

                    <!-- DeviceSamples and Status -->
                    <v-card-text>
                        <div class="d-flex justify-space-between align-center">
                            <div>Periodicidad: {{ device.periodicity }} min</div>
                            <v-chip 
                                :color="getDeviceStatus(device).color" 
                                text-color="white" 
                                size="small" 
                                class="rounded px-2"
                            >
                                {{ getDeviceStatus(device).text }}
                            </v-chip>
                        </div>

                        <div class="mt-2 d-flex justify-space-between align-center">
                            <div>Mediciones: {{ getExpectedMeasurements(device).actual }}/{{ getExpectedMeasurements(device).expected }}</div>
                            <div class="text-body-2">{{ getCompletionPercentage(device) }}% Completado</div>
                        </div>

                        <v-progress-linear 
                            :model-value="getCompletionPercentage(device)" 
                            height="8" 
                            rounded
                            :color="getCompletionPercentage(device) >= 70 ? 'purple' : 'primary'"
                            class="mt-1"
                        ></v-progress-linear>
                    </v-card-text>

                    <!-- Updates and Status Info -->
                    <v-card-text class="d-flex justify-space-between pt-0">
                        <div class="d-flex align-center">
                            <v-icon :color="device.active ? 'green' : 'grey'" size="small" class="mr-1">
                                {{ device.active ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                            </v-icon>
                            <span class="text-caption">{{ device.active ? 'Activo' : 'Inactivo' }}</span>
                        </div>
                        <div class="d-flex align-center">
                            <span class="mr-2">Actualizaciones</span>
                            <v-icon size="small" class="mr-1">mdi-message-outline</v-icon>
                            <span>{{ getTotalUpdates(device) }}</span>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
.avatar-group {
    margin-right: -10px;
    border: 2px solid white;
}

.avatar-overlap {
    position: absolute;
    bottom: -40px;
    left: 24px;
    border: 4px solid white;
    background-color: white;
    z-index: 2;
}

.gradient-overlay {
    position: relative;
}

.gradient-overlay::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(0, 0, 0, 0.3));
}
</style>